// `$fetch` — глобал ofetch, из '#imports' он не резолвится (unimport падает).
import { useAsyncData } from '#imports';
import type { RutubeMeta } from './types';
import { parseRutubeSrc } from './utils';

/**
 * URL постера видео из `/expertise/api/rutube/{id}/`.
 *
 * Название и длительность у Rutube тоже есть, но постер уже содержит их
 * впечатанными в кадр — своя подпись поверх дала бы двойной заголовок. Поэтому
 * из ответа берём только `thumbnail`, а название/длительность на карточке —
 * это исключительно то, что задал редактор в CMS.
 *
 * Нужен, потому что `data-poster` в CMS проставляют руками и чаще всего не
 * проставляют вовсе — без постера карточка вырождается в чёрный прямоугольник,
 * а LCP-элементом становится что-то случайное.
 *
 * Запрос идёт на SSR (постер обязан быть в HTML первого экрана, иначе он не
 * станет LCP-кандидатом). Ответ nitro-эндпоинта кэшируется на час со `swr`,
 * так что в проде это почти всегда попадание в кэш, а не поход в Rutube.
 * Ошибку глушим: без постера карточка просто останется прежней.
 */
/**
 * Ручка Nitro. Лежит не в `/api/**`: на контурах nginx отдаёт его в Bitrix, где
 * такой ручки нет, и при переходе без перезагрузки браузер получал 404. Адрес
 * под `/expertise/` до node доезжает; слеш в конце обязателен.
 */
const RUTUBE_META_URL = '/expertise/api/rutube/';

export async function useRutubePoster(src: string): Promise<string> {
  const { id, token } = parseRutubeSrc(src);
  if (!id) return '';

  // Ключ включает токен: приватное и публичное видео с одним id — разные ответы.
  // Одно и то же видео дважды на странице — один запрос (useAsyncData дедуплицирует).
  const key = `rutube:${id}${token ? `:${token}` : ''}`;

  const { data } = await useAsyncData<string>(key, async () => {
    try {
      const res = await $fetch<{ data?: RutubeMeta }>(`${RUTUBE_META_URL}${id}/`, {
        query: token ? { p: token } : undefined,
      });
      // Из всего ответа карточке нужен только постер — остальное Nuxt зря
      // сериализовал бы в payload страницы.
      return res?.data?.thumbnail ?? '';
    } catch {
      // 403/404/502 — видео закрыто, удалено или Rutube лёг. Не роняем страницу.
      return '';
    }
  });

  return data.value ?? '';
}
